import { useCallback, useEffect, useState } from 'react'
import { useApi } from '@/hooks/useApi'
import { apiClient } from '@/api/client'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

// AI-CALL-AUDIT-1D R5 — split out of MonitorPage.tsx to keep that file under
// its size budget. Reads only already-persisted ai.call audit rows via
// /v1/admin/ai-summary; no new token-based computation happens here.

interface EngagementRow {
  engagement_ref: string | null
  engagement_name: string | null
  product: string | null
  calls: number
  cost_usd: number
  fee: number | null
  cost_pct_of_fee: number | null
}

interface ActorRow {
  actor: string
  calls: number
  cost_usd: number
}

interface ModelRow {
  model: string
  calls: number
  cost_usd: number
}

interface ProductRow {
  product: string | null
  calls: number
  cost_usd: number
}

interface AiSpendSummary {
  environment: string
  calls: number
  cost_usd: number
  unparsed: number
  by_outcome: Record<string, number>
  by_engagement: EngagementRow[]
  by_actor: ActorRow[]
  by_model: ModelRow[]
  by_product: ProductRow[]
}

type RangeKey = 'today' | '7d' | '30d' | 'all'

const RANGES: { key: RangeKey; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: 'all', label: 'All time' },
]

function fmtUsd(n: number) {
  return `$${n.toFixed(4)}`
}

function fmtPct(n: number | null) {
  // n is already a percentage value (e.g. 0.0001 means 0.0001% of fee) —
  // computed server-side in core.platform.ai_spend, never re-scaled here.
  if (n === null) return '—'
  if (n > 0 && n < 0.0001) return '< 0.0001%'
  return `${n.toFixed(4)}%`
}

function fmtFeeUsd(n: number | null) {
  if (n === null) return '—'
  return `$${Math.round(n).toLocaleString('en-US')}`
}

function engagementLabel(row: EngagementRow) {
  // '—' is reserved for missing fee / % of fee — an ai.call with no
  // engagement is a real, named state, not a missing value.
  if (!row.engagement_ref) return 'No engagement'
  return row.engagement_name ?? row.engagement_ref
}

function productLabel(product: string | null) {
  // A NULL product (rows written before 1B) is a real, named state — not a
  // blank cell.
  return product || 'unknown'
}

export function AiSpendSection() {
  const { data, loading, error, execute } = useApi<AiSpendSummary>()
  const [range, setRange] = useState<RangeKey>('today')

  const load = useCallback(
    (r: RangeKey) => {
      execute(() =>
        apiClient
          .get<AiSpendSummary>('/v1/admin/ai-summary', { params: { range: r } })
          .then(res => res.data),
      )
    },
    [execute],
  )

  useEffect(() => {
    load(range)
  }, [load, range])

  const summary = data

  return (
    <section data-testid="ai-spend-section">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold text-gray-900">
          AI Spend{' '}
          {summary && (
            <span className="text-sm font-normal text-gray-500">({summary.environment})</span>
          )}
        </h2>
        <div className="flex gap-1">
          {RANGES.map(r => (
            <button
              key={r.key}
              type="button"
              data-testid={`ai-spend-range-${r.key}`}
              onClick={() => setRange(r.key)}
              className={`px-2 py-1 text-xs rounded ${
                range === r.key ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="text-sm text-red-600">Failed to load AI spend.</div>}
      {loading && <p className="text-sm text-gray-500">Loading…</p>}

      {summary && !loading && summary.calls === 0 && (
        <p data-testid="ai-spend-empty" className="text-sm text-gray-500">
          No AI calls in this range.
        </p>
      )}

      {summary && !loading && summary.calls > 0 && (
        <>
          <div
            data-testid="ai-spend-totals"
            className="flex flex-wrap gap-3 rounded-lg border bg-gray-50 px-4 py-3 text-sm mb-3"
          >
            <span>
              {summary.calls} calls · {fmtUsd(summary.cost_usd)}
            </span>
            <span className="text-gray-300">|</span>
            <span>
              ok {summary.by_outcome.ok ?? 0} · refused {summary.by_outcome.refused_cap ?? 0} ·
              error {summary.by_outcome.error ?? 0}
            </span>
          </div>

          <div className="border rounded-lg overflow-x-auto mb-3">
            <Table data-testid="ai-spend-by-engagement">
              <TableHeader>
                <TableRow>
                  <TableHead>Engagement</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Calls</TableHead>
                  <TableHead>Cost</TableHead>
                  <TableHead>Fee</TableHead>
                  <TableHead>% of fee</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summary.by_engagement.map((row, i) => (
                  <TableRow key={`${row.engagement_ref ?? 'none'}-${i}`}>
                    <TableCell className="text-sm">{engagementLabel(row)}</TableCell>
                    <TableCell className="text-sm">{productLabel(row.product)}</TableCell>
                    <TableCell className="text-sm">{row.calls}</TableCell>
                    <TableCell className="text-sm font-mono">{fmtUsd(row.cost_usd)}</TableCell>
                    <TableCell className="text-sm font-mono">{fmtFeeUsd(row.fee)}</TableCell>
                    <TableCell className="text-sm">{fmtPct(row.cost_pct_of_fee)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="border rounded-lg overflow-x-auto">
              <Table data-testid="ai-spend-by-actor">
                <TableHeader>
                  <TableRow>
                    <TableHead>Person</TableHead>
                    <TableHead>Calls</TableHead>
                    <TableHead>Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summary.by_actor.map(row => (
                    <TableRow key={row.actor}>
                      <TableCell className="text-sm">{row.actor}</TableCell>
                      <TableCell className="text-sm">{row.calls}</TableCell>
                      <TableCell className="text-sm font-mono">{fmtUsd(row.cost_usd)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="border rounded-lg overflow-x-auto">
              <Table data-testid="ai-spend-by-model">
                <TableHeader>
                  <TableRow>
                    <TableHead>Model</TableHead>
                    <TableHead>Calls</TableHead>
                    <TableHead>Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summary.by_model.map(row => (
                    <TableRow key={row.model}>
                      <TableCell className="text-sm">{row.model}</TableCell>
                      <TableCell className="text-sm">{row.calls}</TableCell>
                      <TableCell className="text-sm font-mono">{fmtUsd(row.cost_usd)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="border rounded-lg overflow-x-auto">
              <Table data-testid="ai-spend-by-product">
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead>Calls</TableHead>
                    <TableHead>Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summary.by_product.map(row => (
                    <TableRow key={row.product ?? 'unknown'}>
                      <TableCell className="text-sm">{productLabel(row.product)}</TableCell>
                      <TableCell className="text-sm">{row.calls}</TableCell>
                      <TableCell className="text-sm font-mono">{fmtUsd(row.cost_usd)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </>
      )}
    </section>
  )
}
